import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, Search, BrainCircuit, RefreshCw, FileText, Plus, Zap } from "lucide-react";
import { BreakdownBar, SpecChip } from "@/pages/fleet/RecBreakdown";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import EmbeddedForms from "@/components/forms/EmbeddedForms";
import CreateVisitModal from "@/components/fleet/CreateVisitModal";
import ProviderFinder from "@/components/fleet/ProviderFinder";
import LiveVisitTracker from "@/components/fleet/LiveVisitTracker";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useVisitTracking } from "@/hooks/useVisitTracking";
import {
  VISIT_TYPES, VISIT_STATUS_COLORS, VISIT_PRIORITY_COLORS, SOURCE_LABELS, timeAgo, READINESS_COLORS,
} from "@/pages/fleet/constants";

const WRITE_ROLES = ["admin", "hah_coordinator", "care_coordinator", "clinical_director"];
// Status transitions that should trigger GPS tracking on the clinician's device
const TRACKED_STATUSES = new Set(["en_route", "arrived", "in_progress"]);

function _isMyVisit(visit, user) {
  if (!user || !visit) return false;
  if (visit.clinician_id === user.id) return true;
  const ids = Array.isArray(visit.clinician_ids) ? visit.clinician_ids : [];
  return ids.includes(user.id);
}

export default function FleetVisits() {
  const { user } = useAuth();
  const canCreate = WRITE_ROLES.includes(user?.role_code);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("__open__");
  const [priorityFilter, setPriorityFilter] = useState("__all__");
  const [sourceFilter, setSourceFilter] = useState("__all__");
  const [recOpen, setRecOpen] = useState(null);
  const [recItems, setRecItems] = useState([]);
  const [recBusy, setRecBusy] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [placedForms, setPlacedForms] = useState([]);
  const [formsVisit, setFormsVisit] = useState(null);
  const [visitForms, setVisitForms] = useState([]);
  const [visitFormsLoading, setVisitFormsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [pfOpen, setPfOpen] = useState(false);

  const finderRequest = recOpen ? {
    day: (recOpen.scheduled_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
    role: null,
    skills: recOpen.required_skill ? [recOpen.required_skill] : [],
    medical_specialty: recOpen.medical_specialization || null,
    sub_specialty: recOpen.sub_specialty || null,
    specialty_type: recOpen.specialty_type || null,
    patient_id: recOpen.patient_id || null,
    duration: recOpen.duration_minutes || 60,
  } : null;

  useEffect(() => {
    api.get("/forms/placed", { params: { module: "fleet" } })
      .then((r) => setPlacedForms(r.data.items)).catch(() => {});
  }, []);

  // Load the specific forms attached to the visit being opened
  useEffect(() => {
    if (!formsVisit || !(formsVisit.form_ids || []).length) { setVisitForms([]); return; }
    setVisitFormsLoading(true);
    api.get("/forms/published-by-ids", { params: { ids: formsVisit.form_ids.join(",") } })
      .then((r) => setVisitForms(r.data.items || []))
      .catch(() => setVisitForms([]))
      .finally(() => setVisitFormsLoading(false));
  }, [formsVisit]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (statusFilter !== "__open__" && statusFilter !== "__all__") params.status = statusFilter;
      if (priorityFilter !== "__all__") params.priority = priorityFilter;
      if (sourceFilter !== "__all__") params.source_module = sourceFilter;
      const r = await api.get("/fleet/visits", { params });
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter, priorityFilter, sourceFilter]);
  useEffect(() => { load(); }, [load]);

  // If the provider reloads this page mid-visit, transparently resume the
  // existing GPS tracking session for any of their own visits still on the road.
  useEffect(() => {
    if (loading || tracking.isTracking || !user?.id) return;
    const mine = items.find((v) =>
      _isMyVisit(v, user) &&
      (v.status === "en_route" || v.status === "arrived" || v.status === "in_progress"));
    if (!mine) return;
    (async () => {
      const resumed = await tracking.resumeIfActive(mine.id);
      if (resumed) setActiveVisitId(mine.id);
    })();
  }, [loading, items, user?.id]);

  const filtered = items.filter((v) => {
    if (statusFilter === "__open__" && ["completed", "cancelled"].includes(v.status)) return false;
    if (search) {
      const hay = `${v.patient_name || ""} ${v.patient_mrn || ""} ${v.visit_type} ${v.required_skill || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  async function openRecommendations(visit) {
    setRecOpen(visit); setRecBusy(true); setRecItems([]); setNarrative(null);
    try {
      const r = await api.post(`/fleet/visits/${visit.id}/recommend`);
      setRecItems(r.data.recommendations || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function fetchNarrative() {
    if (!recOpen) return;
    setRecBusy(true);
    try {
      const r = await api.post(`/fleet/visits/${recOpen.id}/recommend/explain`);
      setNarrative(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecBusy(false); }
  }
  async function assign(clinicianId, ai_accepted) {
    try {
      await api.post(`/fleet/visits/${recOpen.id}/assign`, { clinician_id: clinicianId, ai_accepted });
      toast.success("Visit assigned");
      setRecOpen(null); setRecItems([]); setNarrative(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  const tracking = useVisitTracking();
  const [activeVisitId, setActiveVisitId] = useState(null);

  async function statusAction(visit, status, payload = {}) {
    try {
      // If the current user IS the assigned clinician and this is an "on-visit"
      // transition, kick off GPS tracking on their device before the status
      // change. Dispatchers/coordinators marking someone else's visit are NOT
      // asked for GPS.
      if (_isMyVisit(visit, user) && TRACKED_STATUSES.has(status) && !tracking.isTracking) {
        try {
          const siteLat = visit.address?.lat ?? visit.address?.latitude;
          const siteLng = visit.address?.lng ?? visit.address?.longitude;
          await tracking.startTracking({
            visitId: visit.id, kind: "fleet",
            label: visit.patient_name || visit.patient_mrn || visit.id,
            siteLatitude: typeof siteLat === "number" ? siteLat : undefined,
            siteLongitude: typeof siteLng === "number" ? siteLng : undefined,
          });
          setActiveVisitId(visit.id);
        } catch (e) {
          toast.error(e.message || "Location access is required to start the visit.");
          return;
        }
      }
      if (status === "completed" || status === "cancelled") {
        try { await tracking.endTracking(); } catch { /* non-fatal */ }
        setActiveVisitId(null);
      }
      await api.post(`/fleet/visits/${visit.id}/status`, { status, ...payload });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  const [autoBusy, setAutoBusy] = useState(null);
  async function autoAssign(visit) {
    setAutoBusy(visit.id);
    try {
      const r = await api.post(`/fleet/visits/${visit.id}/auto-assign`);
      const name = r.data?.recommendation?.clinician_name || "best match";
      toast.success(`Auto-assigned to ${name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAutoBusy(null); }
  }

  return (
    <div data-testid="fleet-visits-page">
      {/* Live GPS tracking overlay — only visible when this user has a live session */}
      <LiveVisitTracker
        tracking={tracking}
        onEndVisit={async () => {
          if (!activeVisitId) return;
          try {
            await api.post(`/fleet/visits/${activeVisitId}/status`, { status: "completed" });
            toast.success("Visit completed");
            setActiveVisitId(null);
            load();
          } catch (e) { toast.error(formatApiError(e)); }
        }}
      />
      <PageHeader title="Visit Scheduling Board" subtitle="Visits sourced from Onboarding, RPM, Hotline and manual requests."
        actions={canCreate ? (
          <Button onClick={() => setShowCreate(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="fleet-create-visit-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Create Visit
          </Button>
        ) : null}
      />
      <div className="hah-card p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by patient, MRN, visit type…"
            className="bg-transparent outline-none text-sm w-full" data-testid="fleet-visits-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="fleet-visits-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__open__">Open only</SelectItem>
            <SelectItem value="__all__">All</SelectItem>
            {Object.entries(VISIT_STATUS_COLORS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All priority</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="routine">Routine</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sources</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} className="border-[#E5E7EB]"><RefreshCw className="w-3.5 h-3.5" /></Button>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]">
          <ClipboardList className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" /> No visits match the current filters.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((v) => {
            const sc = VISIT_STATUS_COLORS[v.status] || {};
            const pc = VISIT_PRIORITY_COLORS[v.priority] || {};
            return (
              <div key={v.id} className="hah-card p-4" data-testid={`visit-row-${v.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: pc.bg, color: pc.text, border: `1px solid ${pc.border}40` }}>
                        {pc.label}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider" style={{ background: sc.bg, color: sc.text }}>
                        {sc.label}
                      </span>
                      <span className="text-[10px] uppercase font-semibold text-[#6B7280]">{SOURCE_LABELS[v.source_module]}</span>
                    </div>
                    <div className="text-sm font-semibold text-[#1F2937]">{VISIT_TYPES[v.visit_type] || v.visit_type}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      {v.patient_name} <span className="font-mono">({v.patient_mrn})</span>
                      {v.required_skill && <> · needs <strong>{v.required_skill}</strong></>}
                      <> · created {timeAgo(v.created_at)}</>
                    </div>
                    {v.clinician_name && (
                      <div className="text-xs text-[#0B7E8C] mt-0.5">Assigned: {v.clinician_name}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {v.status === "unassigned" && (
                      <Button size="sm" onClick={() => openRecommendations(v)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white text-xs h-7" data-testid={`visit-assign-${v.id}`}>
                        <BrainCircuit className="w-3 h-3 mr-1" /> AI Assign
                      </Button>
                    )}
                    {v.status === "unassigned" && (
                      <Button size="sm" variant="outline" disabled={autoBusy === v.id} onClick={() => autoAssign(v)}
                        className="border-[#0B7E8C33] text-[#0B7E8C] text-xs h-7" data-testid={`visit-auto-assign-${v.id}`}>
                        <Zap className="w-3 h-3 mr-1" /> {autoBusy === v.id ? "Assigning…" : "Auto-assign best"}
                      </Button>
                    )}
                    {v.status === "assigned" && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "en_route")} className="border-[#E5E7EB] text-xs h-7">En route</Button>
                    )}
                    {v.status === "en_route" && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "arrived")} className="border-[#E5E7EB] text-xs h-7">Arrived</Button>
                    )}
                    {(v.status === "arrived" || v.status === "assigned") && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "in_progress")} className="border-[#E5E7EB] text-xs h-7">Start</Button>
                    )}
                    {(v.status === "in_progress" || v.status === "arrived") && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "completed")} className="border-[#10B98133] text-[#10B981] text-xs h-7">Complete</Button>
                    )}
                    {!["completed", "cancelled"].includes(v.status) && (
                      <Button size="sm" variant="outline" onClick={() => statusAction(v, "cancelled")} className="border-[#EF444433] text-[#EF4444] text-xs h-7">Cancel</Button>
                    )}
                    {(v.form_ids?.length > 0) && (
                      <Button size="sm" variant="outline" onClick={() => setFormsVisit(v)} className="border-[#0B7E8C33] text-[#0B7E8C] text-xs h-7" data-testid={`visit-forms-${v.id}`}>
                        <FileText className="w-3 h-3 mr-1" /> Forms ({v.form_ids.length})
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI recommendation dialog */}
      <Dialog open={!!recOpen} onOpenChange={(o) => { if (!o) { setRecOpen(null); setRecItems([]); setNarrative(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>AI Clinician Recommendations</DialogTitle></DialogHeader>
          {recBusy && recItems.length === 0 && <div className="py-4 text-sm text-[#6B7280]">Computing recommendations…</div>}
          {!recBusy && recItems.length === 0 && <div className="py-4 text-sm text-[#94A3B8]">No clinicians available.</div>}
          <div className="space-y-2 max-h-[420px] overflow-auto">
            {recItems.map((r, idx) => (
              <div key={r.clinician_id} className={`rounded-lg border p-3 ${idx === 0 ? "border-[#0B7E8C] bg-[#EAF8FA]" : "border-[#E5E7EB] bg-white"}`}
                data-testid={`fleet-visit-rec-${idx}`}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-sm font-semibold text-[#1F2937]">{r.clinician_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{(r.role_code || "").replace("_", " ")}</div>
                  </div>
                  <div className="text-2xl font-bold text-[#0B7E8C]">{r.score}</div>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-[#475569]">
                  <span>{r.distance_km}km · {r.eta_minutes}min</span>
                  <span>Load {r.workload_pct}%</span>
                  <span style={{ color: (READINESS_COLORS[r.readiness] || {}).text }}>{(READINESS_COLORS[r.readiness] || {}).label}</span>
                  {r.within_radius === false && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF2F2] text-[#991B1B] font-semibold">Out of range</span>
                  )}
                </div>
                {(r.medical_specialization || r.sub_specialty || r.specialty_type) && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`fleet-visit-rec-specialty-${idx}`}>
                    {r.medical_specialization && <SpecChip ok={r.specialty_match?.medical} label={r.medical_specialization} />}
                    {r.sub_specialty && <SpecChip ok={r.specialty_match?.sub} label={r.sub_specialty} />}
                    {r.specialty_type && <SpecChip ok={r.specialty_match?.level} label={r.specialty_type} />}
                  </div>
                )}
                <div className="mt-2"><BreakdownBar breakdown={r.breakdown} /></div>
                <div className="text-[11px] text-[#6B7280] mt-1">{r.reasons?.join(" · ")}</div>
                <Button size="sm" onClick={() => assign(r.clinician_id, idx === 0)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white text-xs h-7 mt-2"
                  data-testid={`fleet-visit-rec-assign-${idx}`}>Assign {idx === 0 ? "(accept AI)" : ""}</Button>
              </div>
            ))}
          </div>
          {recItems.length > 0 && (
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={fetchNarrative} className="w-full border-[#8B5CF6] text-[#8B5CF6]" data-testid="fleet-visit-rec-explain">
                Why this pick? (LLM)
              </Button>
              {narrative && (
                <div className="mt-2 p-2.5 rounded bg-[#F5F3FF] border border-[#8B5CF6]/30 text-xs text-[#1F2937]">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8B5CF6] mb-1">
                    {narrative.model === "fallback" ? "Deterministic" : narrative.model}
                  </div>
                  {narrative.narrative}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPfOpen(true)} className="border-[#0B7E8C] text-[#0B7E8C]" data-testid="fleet-visit-timeline-finder">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Timeline finder
            </Button>
            <Button variant="outline" onClick={() => setRecOpen(null)} className="border-[#E5E7EB]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProviderFinder open={pfOpen} onOpenChange={setPfOpen}
        title={recOpen ? `Find provider for ${recOpen.patient_name || "visit"}` : "Find provider"}
        request={pfOpen ? finderRequest : null}
        onPick={(cid) => { setPfOpen(false); assign(cid, false); }} />

      <Sheet open={!!formsVisit} onOpenChange={(o) => { if (!o) setFormsVisit(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-[680px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>Visit Forms</SheetTitle>
            <SheetDescription>{formsVisit ? `${formsVisit.patient_name} · ${formsVisit.patient_mrn}` : ""}</SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {visitFormsLoading && <div className="text-sm text-[#6B7280] py-8 text-center">Loading forms…</div>}
            {!visitFormsLoading && formsVisit && visitForms.length === 0 && (
              <div className="text-sm text-[#6B7280] py-8 text-center">No forms were selected for this visit.</div>
            )}
            {!visitFormsLoading && formsVisit && visitForms.length > 0 && (
              <EmbeddedForms module="patient" entityId={formsVisit.patient_id}
                entityLabel={`${formsVisit.patient_name} (${formsVisit.patient_mrn})`}
                forms={visitForms}
                extraContext={{ visit_id: formsVisit.id, visit_type: formsVisit.visit_type }} />
            )}
          </div>
        </SheetContent>
      </Sheet>

      <CreateVisitModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
    </div>
  );
}
